import { apiClient } from '@/services/'

export const namespaced = true

export const state = {
  loadingPage: false,
  loadingPageCount: 0,
  loadingTable: false,
  tableData: [],
  footerData: [],
  projectedEvaluations: [],
  pendingEvaluations: [],
  evalTableData: [],
  currentEvaluation: {},
  currentEvalEdited: false,
  strengthsBarriers: [],
  goals: [],
}

export const mutations = {
  // Used when multiple async calls need to finish before the page loads -MN
  ADD_TO_LOADING_COUNT(state) {
    state.loadingPageCount += 1
    state.loadingPage = true
  },
  SUBTRACT_FROM_LOADING_COUNT(state) {
    state.loadingPageCount -= 1
    if (state.loadingPageCount == 0) {
      state.loadingPage = false
    }
  },
  SET_LOADING_TABLE(state, status) {
    state.loadingTable = status
  },
  SET_PROJECTED_EVALS(state, status) {
    state.projectedEvaluations = status
  },
  SET_PENDING_EVALS(state, status) {
    state.pendingEvaluations = status
  },
  SET_CURRENT_EVAL(state, status) {
    state.currentEvaluation = status
  },
  SET_EVAL_EDITED(state, status) {
    state.currentEvalEdited = status
  },
  SET_EVAL_TABLE_DATA(state, status) {
    state.evalTableData = status
  },
  SET_FORMATTED_TABLE_DATA(state, status) {
    state.tableData = status
  },
  SET_FORMATTED_FOOTER_DATA(state, status) {
    state.footerData = status
  },
  SET_STRENGTHS_BARRIERS(state, status) {
    state.strengthsBarriers = status
  },
  SET_GOALS(state, status) {
    state.goals = status
  },
}

export const actions = {
  getEvaluationsData({ dispatch, getters }) {
    dispatch('getStrengthsBarriers')
    dispatch('getGoals')
    dispatch('getAllEvaluations').then(() => {
      if (getters.allEvaluations.length > 0) {
        dispatch('getEvalDetails', getters.allEvaluations[0])
      }
    })
  },
  // Evaluations
  async getAllEvaluations({ dispatch, getters, rootState }) {
    await dispatch('getPendingEvaluations')
    await dispatch('getProjectedEvaluations')
    // Has ETARs - MN
    if (getters.allEvaluations.length > 0) {
      rootState.userInfo.userRole.push(5)
    }
  },
  getPendingEvaluations({ commit, rootState }) {
    return new Promise((resolve) => {
      commit('ADD_TO_LOADING_COUNT')
      return apiClient
        .get('/PendingEvaluationsListByAdName', {
          params: {
            sAdName: rootState.adName,
          },
        })
        .then((response) => {
          const PendingEvals = response.data.map((x) => {
            return {
              evalStatus: 'Pending',
              ...x,
            }
          })
          commit('SET_PENDING_EVALS', sortEvaluations(PendingEvals))
          commit('SUBTRACT_FROM_LOADING_COUNT')
          resolve()
        })
    })
  },
  getProjectedEvaluations({ commit, rootState }) {
    return new Promise((resolve) => {
      commit('ADD_TO_LOADING_COUNT')
      return apiClient
        .get('/GetProjectedEvaluationsByStudentAdName', {
          params: {
            sAdName: rootState.adName,
          },
        })
        .then((response) => {
          const ProjectedEvals = response.data.map((x) => {
            return {
              evalStatus: 'Projected',
              ...x,
            }
          })
          commit('SET_PROJECTED_EVALS', sortEvaluations(ProjectedEvals))
          commit('SUBTRACT_FROM_LOADING_COUNT')
          resolve()
        })
    })
  },
  getEvalDetails({ commit }, currentEval) {
    commit('SET_CURRENT_EVAL', currentEval)
    commit('SET_LOADING_TABLE', true)
    return apiClient
      .get('/EvaluationFormPartial', {
        params: {
          EvalDetailId: currentEval.evalDetId,
          EvalSummaryId: currentEval.evalSummaryId,
          StudId: currentEval.studId,
          StaffId: currentEval.staffId,
        },
      })
      .then((response) => {
        commit('SET_EVAL_TABLE_DATA', response.data)
        const currentEvalData = response.data,
          // FORMAT TABLE DATA - REFORMAT data from API Call - MN
          staffData = currentEvalData.listOfModels.map((item) => {
            return {
              id: item.evalFactorId,
              Sort: item.factorSequence,
              Factor: item.evalFactorDesc,
              StaffRating: item.evalScoreId,
            }
          }),
          studentData = currentEvalData.listOfStudentModels.map((item) => {
            return {
              id: item.evalFactorId,
              StudentRating: item.evalScoreId,
            }
          }),
          tableData = staffData.map((staff) => ({
            ...staff,
            ...studentData.find((student) => student.id === staff.id),
          })),
          // FORMAT FOOTER DATA - Find and return last modified model
          // Staff
          staffLastModModel = currentEvalData.listOfModels.reduce((a, b) => {
            return new Date(a.lastModDt) > new Date(b.lastModDt) ? a : b
          }),
          // Student
          studentLastModModel =
            currentEvalData.listOfStudentModels.length > 0
              ? currentEvalData.listOfStudentModels.reduce((a, b) => {
                  return new Date(a.lastModDt) > new Date(b.lastModDt) ? a : b
                })
              : {
                  lastModUser: '',
                  lastModDt: null,
                },
          // Format data for table footer
          footerData = [
            {
              user: 'Staff',
              average: currentEvalData.listOfModelsAverage,
              total: currentEvalData.listOfModelsTotal,
              comments: currentEvalData.comments,
              editedComment: false,
              lastModUser: staffLastModModel.lastModUser,
              lastModDt: staffLastModModel.lastModDt,
            },
            {
              user: 'Student',
              average: currentEvalData.listOfStudentModelsAverage,
              total: currentEvalData.listOfStudentModelsTotal,
              comments: currentEvalData.studComments,
              lastModUser: studentLastModModel.lastModUser,
              lastModDt: studentLastModModel.lastModDt,
            },
          ]
        commit('SET_FORMATTED_TABLE_DATA', tableData)
        commit('SET_FORMATTED_FOOTER_DATA', footerData)
        commit('SET_LOADING_TABLE', false)
      })
  },
  // Strengths / Barriers
  getStrengthsBarriers({ commit, rootState }) {
    commit('ADD_TO_LOADING_COUNT')
    return apiClient
      .get('/EvaluationStrengthsBarriers', {
        params: {
          sAdName: rootState.adName,
        },
      })
      .then((response) => {
        return apiClient
          .get('/GetStrengthsBarriersList', {
            params: {
              EnrId: response.data.enrId,
              ActiveInactiveFilter: 'A',
            },
          })
          .then((response) => {
            commit('SET_STRENGTHS_BARRIERS', response.data)
            commit('SUBTRACT_FROM_LOADING_COUNT')
          })
      })
  },
  // Goals
  getGoals({ commit, rootState }) {
    commit('ADD_TO_LOADING_COUNT')
    return apiClient
      .get('/EvaluationGoals', {
        params: {
          sAdName: rootState.adName,
        },
      })
      .then((response) => {
        return apiClient
          .get('/GetGoalsList', {
            params: {
              EnrId: response.data.enrId,
            },
          })
          .then((response) => {
            // Save goals
            commit('SET_GOALS', response.data)
            commit('SUBTRACT_FROM_LOADING_COUNT')
          })
      })
  },
}

export const getters = {
  goalsList: (state) => {
    return state.goals.map((g) => g.goalDesc)
  },
  allEvaluations: (state) => {
    return [...state.pendingEvaluations, ...state.projectedEvaluations]
  },
}

function sortEvaluations(item) {
  item.sort(function (a, b) {
    // Date, Area, Description
    return (
      new Date(a.evalSchedDt) - new Date(b.evalSchedDt) ||
      a.evalArea.localeCompare(b.evalArea) ||
      a.evalDetailDesc.localeCompare(b.evalDetailDesc)
    )
  })

  return item
}
